class NotificationService {
  constructor() {
    this.preferences = this.getPreferences()
    this.audioContext = null
    this.notificationSound = null
    this.initializeAudio()
  }

  getPreferences() {
    const stored = localStorage.getItem('notification-preferences')
    return stored ? JSON.parse(stored) : {
      newChat: true,
      newMessage: true,
      email: true,
      sound: true,
      desktop: true,
      emailDigest: false
    }
  }

  updatePreferences(newPreferences) {
    this.preferences = { ...this.preferences, ...newPreferences }
  }

  async initializeAudio() {
    try {
      // Create a simple notification sound using Web Audio API
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
    } catch (error) {
      console.warn('Web Audio API not supported:', error)
    }
  }

  playNotificationSound() {
    if (!this.preferences.sound || !this.audioContext) return

    try {
      const oscillator = this.audioContext.createOscillator()
      const gainNode = this.audioContext.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(this.audioContext.destination)

      oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime)
      oscillator.frequency.setValueAtTime(600, this.audioContext.currentTime + 0.1)
      
      gainNode.gain.setValueAtTime(0.3, this.audioContext.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3)

      oscillator.start(this.audioContext.currentTime)
      oscillator.stop(this.audioContext.currentTime + 0.3)
    } catch (error) {
      console.warn('Failed to play notification sound:', error)
    }
  }

  async showDesktopNotification(title, options = {}) {
    if (!this.preferences.desktop) return

    // Check if notifications are supported
    if (!('Notification' in window)) {
      console.warn('Desktop notifications not supported')
      return
    }

    // Request permission if needed
    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        console.warn('Desktop notification permission denied')
        return
      }
    }

    if (Notification.permission === 'granted') {
      const notification = new Notification(title, {
        icon: '/digitaltek.ico',
        badge: '/digitaltek.ico',
        ...options
      })

      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close()
      }, 5000)

      return notification
    }
  }

  // Notification methods for different events
  async notifyNewChat(sessionData) {
    if (!this.preferences.newChat) return

    const title = 'New Chat Request'
    const body = `New chat from User ${sessionData.userId?.slice(-6) || 'Unknown'}`

    this.playNotificationSound()
    
    return this.showDesktopNotification(title, {
      body,
      tag: `new-chat-${sessionData.sessionId}`,
      data: { type: 'new-chat', sessionId: sessionData.sessionId }
    })
  }

  async notifyNewMessage(messageData) {
    if (!this.preferences.newMessage) return

    const title = 'New Message'
    const body = messageData.content?.substring(0, 100) || 'New message received'

    this.playNotificationSound()
    
    return this.showDesktopNotification(title, {
      body,
      tag: `new-message-${messageData.sessionId}`,
      data: { type: 'new-message', sessionId: messageData.sessionId }
    })
  }

  async notifySessionTransfer(sessionData) {
    const title = 'Chat Transferred'
    const body = `Chat session has been transferred to you`

    this.playNotificationSound()
    
    return this.showDesktopNotification(title, {
      body,
      tag: `transfer-${sessionData.sessionId}`,
      data: { type: 'transfer', sessionId: sessionData.sessionId }
    })
  }

  async notifySessionTransferred(transferData) {
    const title = 'Chat Transferred to You'
    const body = `Chat session transferred from ${transferData.fromAgent}`

    this.playNotificationSound()
    
    return this.showDesktopNotification(title, {
      body,
      tag: `transferred-${transferData.sessionId}`,
      data: { type: 'transferred', sessionId: transferData.sessionId }
    })
  }

  async notifyTransferCompleted(transferData) {
    const title = 'Transfer Completed'
    const body = `Chat session successfully transferred to ${transferData.targetAgent}`

    return this.showDesktopNotification(title, {
      body,
      tag: `transfer-completed-${transferData.sessionId}`,
      data: { type: 'transfer-completed', sessionId: transferData.sessionId }
    })
  }

  async notifySessionTimeout(sessionData) {
    const title = 'Chat Session Timeout'
    const body = `Chat session has timed out due to inactivity`

    return this.showDesktopNotification(title, {
      body,
      tag: `timeout-${sessionData.sessionId}`,
      data: { type: 'timeout', sessionId: sessionData.sessionId }
    })
  }

  // Test notification
  async testNotification() {
    this.playNotificationSound()
    
    return this.showDesktopNotification('Test Notification', {
      body: 'This is a test notification from Digitaltek Agent Dashboard',
      tag: 'test-notification'
    })
  }

  // Check if notifications are enabled and permission granted
  isEnabled() {
    return (
      'Notification' in window && 
      Notification.permission === 'granted' && 
      this.preferences.desktop
    )
  }

  // Request notification permission
  async requestPermission() {
    if (!('Notification' in window)) {
      throw new Error('Desktop notifications not supported')
    }

    const permission = await Notification.requestPermission()
    return permission === 'granted'
  }
}

export default new NotificationService()